/**
 * BattleWhatIf.jsx - 1v1 Battle Comparison
 * Beta v2 - Clean side-by-side roster (default) + optional analysis toggle
 * 
 * DIRECTIVE D2: Mandatory null-safety guard for opponent prop
 */

import React, { useState, useEffect } from 'react';
import { ChevronLeft, User, Zap, GitCompare, Percent, TrendingUp, TrendingDown, Minus, BarChart3 } from 'lucide-react';

import { LoadingSpinner, ErrorState } from '../../components/ui';
import { useCompare } from '../../hooks/useGMStrategy';

export const BattleWhatIf = ({ pool, entry, opponent, onBack }) => {
  const userId = localStorage.getItem('current_user_id') || '';
  const { compareData, loading, error, compare, clearCompare } = useCompare(pool?.pool_id, userId);
  const [showAnalysis, setShowAnalysis] = useState(false);
  
  useEffect(() => {
    if (opponent?.userId) {
      compare(opponent.userId);
    }
    return () => clearCompare();
  }, [opponent?.userId, compare, clearCompare]);

  // DIRECTIVE D2: NULL-SAFETY GUARD
  if (!opponent) {
    return (
      <div className="min-h-screen bg-slate-100 pb-20">
        <header className="bg-white shadow-sm px-4 py-3 flex items-center">
          <button onClick={onBack} className="p-2 -ml-2 text-slate-600 min-w-[44px] min-h-[44px] flex items-center justify-center">
            <ChevronLeft size={22} />
          </button>
          <h1 className="text-base font-bold text-slate-900 ml-2">Head-to-Head</h1>
        </header>
        <div className="flex items-center justify-center py-12">
          <p className="text-slate-500">Waiting for opponent data...</p>
        </div>
      </div>
    );
  }

  const myRoster = compareData?.myRoster || entry?.roster || [];
  const oppRoster = compareData?.oppRoster || opponent?.roster || [];
  const myRank = compareData?.myRank || entry?.rank || 127;
  const oppRank = compareData?.oppRank || opponent?.rank || 1;
  const myUnits = compareData?.myTotalUnits || myRoster.reduce((s, r) => s + (r.units || 0), 0);
  const oppUnits = compareData?.oppTotalUnits || oppRoster.reduce((s, r) => s + (r.units || 0), 0);
  const swings = compareData?.swings || [];

  // Analysis metrics (only calculated when needed)
  const exposureDiff = myUnits - oppUnits;
  const maxSwing = swings.reduce((total, swing) => {
    const mySlotUnits = swing.mySlot?.units || swing.myUnits || 0;
    const oppSlotUnits = swing.oppSlot?.units || swing.oppUnits || 0;
    return total + (swing.type === 'opposite' ? mySlotUnits + oppSlotUnits : Math.abs(mySlotUnits - oppSlotUnits));
  }, 0);

  // Build roster comparison
  const buildRosterComparison = () => {
    const oppByPropId = {};
    oppRoster.forEach(slot => {
      const propId = slot.propId || slot.prop?.id;
      if (propId) oppByPropId[propId] = slot;
    });

    const comparisons = [];
    const processedPropIds = new Set();

    myRoster.forEach(slot => {
      if (!slot.player) return;
      const propId = slot.propId || slot.prop?.id;
      const playerName = slot.player?.name || slot.playerName || 'Unknown';
      const propStat = slot.prop?.stat || slot.stat || '';
      const propLine = slot.prop?.line || slot.line || '';
      const mySide = slot.side || '';
      const myDollars = slot.units || 0;

      const oppSlot = propId ? oppByPropId[propId] : null;
      const oppSide = oppSlot?.side || null;
      const oppDollars = oppSlot?.units || 0;

      let outcome = 0;
      let outcomeType = 'neutral';
      if (oppSlot) {
        processedPropIds.add(propId);
        if (mySide !== oppSide && mySide && oppSide) {
          outcome = myDollars + oppDollars;
          outcomeType = 'swing';
        } else if (mySide === oppSide) {
          outcome = myDollars - oppDollars;
          outcomeType = outcome > 0 ? 'advantage' : outcome < 0 ? 'disadvantage' : 'even';
        }
      }

      comparisons.push({ playerName, propStat, propLine, mySide, myDollars, oppSide, oppDollars, outcome, outcomeType, isShared: !!oppSlot });
    });

    oppRoster.forEach(slot => {
      const propId = slot.propId || slot.prop?.id;
      if (processedPropIds.has(propId) || !slot.player) return;
      comparisons.push({
        playerName: slot.player?.name || slot.playerName || 'Unknown',
        propStat: slot.prop?.stat || slot.stat || '',
        propLine: slot.prop?.line || slot.line || '',
        mySide: null,
        myDollars: 0,
        oppSide: slot.side || '',
        oppDollars: slot.units || 0,
        outcome: -(slot.units || 0),
        outcomeType: 'disadvantage',
        isShared: false
      });
    });

    return comparisons;
  };

  const rosterComparison = buildRosterComparison();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 pb-20">
        <header className="bg-white shadow-sm px-4 py-3 flex items-center">
          <button onClick={onBack} className="p-2 -ml-2 text-slate-600 min-w-[44px] min-h-[44px] flex items-center justify-center">
            <ChevronLeft size={22} />
          </button>
          <h1 className="text-base font-bold text-slate-900 ml-2">Loading...</h1>
        </header>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 pb-20">
        <header className="bg-white shadow-sm px-4 py-3 flex items-center">
          <button onClick={onBack} className="p-2 -ml-2 text-slate-600 min-w-[44px] min-h-[44px] flex items-center justify-center">
            <ChevronLeft size={22} />
          </button>
          <h1 className="text-base font-bold text-slate-900 ml-2">Head-to-Head</h1>
        </header>
        <div className="p-4">
          <ErrorState message={error} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-20">
      {/* Header */}
      <header className="bg-white shadow-sm px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          <button onClick={onBack} className="p-2 -ml-2 text-slate-600 min-w-[44px] min-h-[44px] flex items-center justify-center">
            <ChevronLeft size={22} />
          </button>
          <h1 className="text-base font-bold text-slate-900 ml-2">vs {opponent.userId}</h1>
        </div>
        {/* Analysis Toggle */}
        <button 
          onClick={() => setShowAnalysis(!showAnalysis)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors border-2 ${
            showAnalysis ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-indigo-600 border-indigo-600'
          }`}
        >
          <BarChart3 size={16} />
          {showAnalysis ? 'Hide Analysis' : 'Show Analysis'}
        </button>
      </header>

      <main className="p-4 space-y-4">
        {/* User Cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-indigo-600 rounded-xl p-3 text-white">
            <div className="flex items-center gap-2 mb-1">
              <User size={14} />
              <span className="text-xs font-semibold">YOU</span>
            </div>
            <p className="text-lg font-black">{userId}</p>
            <p className="text-xs text-indigo-200">Rank #{myRank} • ${myUnits}</p>
          </div>
          <div className="bg-slate-700 rounded-xl p-3 text-white">
            <div className="flex items-center gap-2 mb-1">
              <User size={14} />
              <span className="text-xs font-semibold">OPPONENT</span>
            </div>
            <p className="text-lg font-black">{opponent.userId}</p>
            <p className="text-xs text-slate-300">Rank #{oppRank} • ${oppUnits}</p>
          </div>
        </div>

        {/* Analysis Section (toggled) */}
        {showAnalysis && (
          <>
            {/* Summary Tiles */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-xl shadow-sm p-3">
                <p className="text-[10px] text-slate-500 uppercase font-semibold">Exposure Differential</p>
                <p className={`text-xl font-black ${exposureDiff >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {exposureDiff >= 0 ? '+' : ''}${exposureDiff}
                </p>
                <p className="text-[10px] text-slate-400">vs opponent's total</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-3">
                <p className="text-[10px] text-slate-500 uppercase font-semibold">Max Swing at Stake</p>
                <p className="text-xl font-black text-amber-600">${maxSwing}</p>
                <p className="text-[10px] text-slate-400">From {swings.length} conflicting pick{swings.length !== 1 ? 's' : ''}</p>
              </div>
            </div>

            {/* Key Swing Picks */}
            {swings.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
                  <div className="flex items-center gap-2">
                    <Zap size={16} className="text-amber-500" />
                    <h3 className="text-sm font-bold text-amber-800">Key Swing Picks</h3>
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {swings.map((swing, i) => {
                    const mySlotUnits = swing.mySlot?.units || swing.myUnits || 0;
                    const oppSlotUnits = swing.oppSlot?.units || swing.oppUnits || 0;
                    const swingValue = swing.type === 'opposite' ? mySlotUnits + oppSlotUnits : Math.abs(mySlotUnits - oppSlotUnits);
                    return (
                      <div key={i} className="p-3 flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${swing.type === 'opposite' ? 'bg-red-100' : 'bg-amber-100'}`}>
                          {swing.type === 'opposite' ? <GitCompare size={16} className="text-red-500" /> : <Percent size={16} className="text-amber-500" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-bold text-slate-800">{swing.playerName}</p>
                          <p className="text-[10px] text-slate-500">
                            {swing.type === 'opposite' 
                              ? `You: ${swing.mySlot?.side || swing.mySide} vs Opp: ${swing.oppSlot?.side || swing.oppSide}`
                              : `You: $${mySlotUnits} vs Opp: $${oppSlotUnits}`}
                          </p>
                        </div>
                        <span className={`text-[10px] px-2 py-1 rounded-full font-semibold ${swing.type === 'opposite' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                          ${swingValue}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* Clean Roster Comparison Table (always shown) */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
            <h3 className="text-sm font-bold text-slate-800">Roster Comparison</h3>
          </div>
          
          {/* Table Header */}
          <div className={`grid gap-1 px-3 py-2 bg-slate-100 text-[10px] font-semibold text-slate-500 uppercase ${showAnalysis ? 'grid-cols-12' : 'grid-cols-10'}`}>
            <div className="col-span-4">Player / Prediction</div>
            <div className="col-span-3 text-center">{userId.substring(0, 6)}</div>
            <div className="col-span-3 text-center">{opponent.userId.substring(0, 6)}</div>
            {showAnalysis && <div className="col-span-2 text-center">Diff</div>}
          </div>
          
          {/* Table Body */}
          <div className="divide-y divide-slate-100">
            {rosterComparison.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">No roster data available</div>
            ) : (
              rosterComparison.map((row, i) => (
                <div key={i} className={`grid gap-1 px-3 py-2 items-center ${showAnalysis ? 'grid-cols-12' : 'grid-cols-10'} ${showAnalysis && row.outcomeType === 'swing' ? 'bg-amber-50' : ''}`}>
                  {/* Player/Prop */}
                  <div className="col-span-4">
                    <p className="text-xs font-semibold text-slate-800 truncate">{row.playerName}</p>
                    <p className="text-xs text-slate-500 truncate">{row.propStat} {row.propLine}</p>
                  </div>
                  
                  {/* Your Side + Dollars */}
                  <div className="col-span-3 text-center">
                    {row.mySide ? (
                      <div className="flex flex-col items-center">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${row.mySide === 'OVER' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-700'}`}>
                          {row.mySide === 'OVER' ? 'O' : 'U'}
                        </span>
                        <p className="text-xs font-semibold text-slate-800">${row.myDollars}</p>
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </div>
                  
                  {/* Opponent Side + Dollars */}
                  <div className="col-span-3 text-center">
                    {row.oppSide ? (
                      <div className="flex flex-col items-center">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${row.oppSide === 'OVER' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-700'}`}>
                          {row.oppSide === 'OVER' ? 'O' : 'U'}
                        </span>
                        <p className="text-xs font-semibold text-slate-800">${row.oppDollars}</p>
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </div>
                  
                  {/* Outcome Differential (only in analysis mode) */}
                  {showAnalysis && (
                    <div className="col-span-2 text-center">
                      {row.outcomeType === 'swing' ? (
                        <div className="flex items-center justify-center gap-0.5">
                          <Zap size={12} className="text-amber-500" />
                          <span className="text-xs font-bold text-amber-600">${row.outcome}</span>
                        </div>
                      ) : row.outcome > 0 ? (
                        <div className="flex items-center justify-center gap-0.5">
                          <TrendingUp size={12} className="text-emerald-500" />
                          <span className="text-xs font-bold text-emerald-600">+${row.outcome}</span>
                        </div>
                      ) : row.outcome < 0 ? (
                        <div className="flex items-center justify-center gap-0.5">
                          <TrendingDown size={12} className="text-slate-400" />
                          <span className="text-xs font-bold text-slate-500">${row.outcome}</span>
                        </div>
                      ) : (
                        <Minus size={12} className="text-slate-400 mx-auto" />
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
};